

































































import { defineComponent, computed, ref, watch } from '@vue/composition-api';
import store from '@/store';
import { snackbar } from '@/components/GlobalSnackbar';
import { RegisterUserPayload } from '@/types';
import { registerNewUserMenu } from '../state';
import { useClient } from '@/use';
import UserPassword from '@/components/UserPassword.vue';

export default defineComponent({
  name: 'RegisterNewUserMenu',
  components: { UserPassword },
  setup() {
    const client = useClient();
    const userTypes = computed(
      () => store.state.application.applicationData?.lists.user_types,
    );
    const userLevels = computed(() => userTypes.value?.map((val, i) => `${i} (${val})`));

    const formValid = ref(false);
    const commonRules = [
      (val: string | number | null) => (val !== '' && val !== null),
    ];

    // Form fields
    const username = ref<string | null>(null);
    const email = ref<string | null>(null);
    const usernameErrorMessage = ref<string | null>(null);

    // Reset error message once value is changed
    watch(username, () => {
      usernameErrorMessage.value = null;
    });

    // Institution values
    const institution = ref<string | null>(null);
    const allInstitutions = computed(
      () => store.state.application.applicationData?.lists.institutions || [],
    );

    const institutions = computed(() => (
      institution.value !== null
        ? allInstitutions.value.filter((v) => v !== institution.value)
        : allInstitutions.value
    ));
    const accessibleInstitutions = ref<string[]>([]);

    // Update accessible institutions when selected institution changes
    // Remove selected institution from list so user can't delete their own institution
    // This is implemented this way to avoid confusion
    watch(institution, (val) => {
      const newInstitutions = accessibleInstitutions.value.slice();
      if (val === null || !newInstitutions.includes(val)) {
        return;
      }

      // Remove institution from list
      newInstitutions.splice(newInstitutions.indexOf(val), 1);
      accessibleInstitutions.value = newInstitutions;
    });

    const password = ref<string | null>(null);
    const userlevel = ref<number | null>(null);
    const usergroup = ref<string | null>(null);

    const updatePW = (val: string) => {
      password.value = val;
    };

    // Set user group default when level changed
    watch(userlevel, (level) => {
      const types = userTypes.value;
      if (level === null || types === undefined) { return; }

      usergroup.value = `${types[level]}s`;
    });

    function setUserLevel(levelStr: string) {
      const level = userLevels.value?.findIndex((str) => levelStr === str);

      if (level !== undefined && level >= 0) {
        userlevel.value = level;
      }
    }

    function closeMenu() { registerNewUserMenu.value = false; }
    async function registerUser() {
      if (
        !username.value
        || !password.value
        || userlevel.value === null
        || !usergroup.value
        || !institution.value
        || !email.value
      ) { return; }

      // Include user's own institution in this list
      const allAccessibleInstitutions = [institution.value, ...accessibleInstitutions.value];
      const payload: RegisterUserPayload = {
        username: username.value,
        password: password.value,
        user_level: userlevel.value,
        user_group: usergroup.value,
        institution: institution.value,
        accessible_institutions: allAccessibleInstitutions,
        email: institution.value,
      };

      const success = await client.registerUser(payload);
      if (!success) {
        usernameErrorMessage.value = 'User already exists';
        return;
      }

      closeMenu();
      snackbar.dispatch({ text: 'User Successfully Registered' });
    }

    return {
      formValid,
      commonRules,
      username,
      email,
      allInstitutions,
      institutions,
      institution,
      accessibleInstitutions,
      usernameErrorMessage,
      userlevel,
      usergroup,
      userLevels,
      setUserLevel,
      closeMenu,
      registerUser,
      UserPassword,
      updatePW,
    };
  },
});
